import { Flex, Text, useToken } from '@tradener/lumen';
import { DateTime } from 'luxon';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { CreditSeriesPointProps } from '@graphql/query/operationLimits/credit/counterpartDetail';

import RoundedSquareIcon from '~/theme/icons/RoundedSquare';

import CounterpartyChartYAxisUnitLabel from '../CounterpartyChartYAxisUnitLabel';
import { buildYearReferenceLines, formatMwmLabel, formatMwmTick, shouldRenderValueLabel } from '../chartFormatters';

export interface CounterpartyMwmChartProps {
  series: CreditSeriesPointProps[];
  activeCompetence?: string;
  onHoverCompetence: (competence?: string) => void;
  shouldAnimate?: boolean;
  hideHeader?: boolean;
}

const COLORS = {
  gray100: 'var(--lumen-colors-gray-100)',
  gray500: 'var(--lumen-colors-gray-500)',
  gray900: 'var(--lumen-colors-gray-900)',
  blue500: 'var(--lumen-colors-blue-500)',
  orange500: 'var(--lumen-colors-orange-500)',
};

const Y_AXIS_WIDTH = 92;
const CHART_HEIGHT = '30rem';
const SYNC_ID = 'operationLimitsDashboardSync';

interface TooltipEntryProps {
  color?: string;
  fill?: string;
  value?: number;
}

export const resolveTooltipIndicatorColor = ({ color, fill, value }: TooltipEntryProps) => {
  if (color) {
    return color;
  }

  if (fill) {
    return fill;
  }

  return Number(value) < 0 ? COLORS.orange500 : COLORS.blue500;
};

function ChartTooltip({ active, payload, label }: any) {
  const [gray0] = useToken('colors', ['gray.0']);

  if (!active || !payload?.length) return null;

  return (
    <Flex bgColor={gray0} py="2" px="3" flexDirection="column" borderRadius="lg" boxShadow="lg" gap="2">
      <Text color="gray.900" textStyle="body2-medium">
        {DateTime.fromISO(label).setLocale('pt-BR').toFormat('MMM yyyy')}
      </Text>
      {payload.map(({ value, name, color, fill }: any) => {
        const indicatorColor = resolveTooltipIndicatorColor({ color, fill, value });

        return (
          <Flex key={name} alignItems="center" justifyContent="space-between" gap="5">
            <Flex alignItems="center" gap="2">
              <Flex alignItems="center" width="3.5" height="3.5" color={indicatorColor}>
                <RoundedSquareIcon />
              </Flex>
              <Text color="gray.500" textStyle="caption1-regular">
                {name}
              </Text>
            </Flex>
            <Text color="gray.900" textStyle="caption1-medium">
              {Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 }).format(value)}
            </Text>
          </Flex>
        );
      })}
    </Flex>
  );
}

function CounterpartyMwmChart({
  series,
  activeCompetence,
  onHoverCompetence,
  shouldAnimate = false,
  hideHeader = false,
}: CounterpartyMwmChartProps) {
  const [gray900] = useToken('colors', ['gray.900']);
  const hasNonZeroValues = series.some((point) => Number(point.value) !== 0);
  const yearReferenceLines = buildYearReferenceLines(series.map(({ competence }) => competence));

  if (!series.length) {
    return (
      <Flex
        data-testid="mwm-chart"
        flexDirection="column"
        bgColor="gray.0"
        height={CHART_HEIGHT}
        width="full"
        borderRadius="xl"
        padding="5"
        color={COLORS.gray900}
      >
        <Flex
          flexDirection="column"
          justifyContent="center"
          gap="0.5"
          visibility={hideHeader ? 'hidden' : 'visible'}
          aria-hidden={hideHeader}
        >
          <Text textStyle="subheading1-semibold">MWm por competência</Text>
        </Flex>
        <Flex flex="1" align="center" justify="center">
          <Text textStyle="body2-regular" color="gray.500">
            -
          </Text>
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex
      data-testid="mwm-chart"
      flexDirection="column"
      bgColor="gray.0"
      height={CHART_HEIGHT}
      width="full"
      borderRadius="xl"
      padding="5"
      color={COLORS.gray900}
    >
      <Flex
        flexDirection="column"
        justifyContent="center"
        gap="0.5"
        visibility={hideHeader ? 'hidden' : 'visible'}
        aria-hidden={hideHeader}
      >
        <Text textStyle="subheading1-semibold">MWm por competência</Text>
      </Flex>
      <Flex
        direction="column"
        width="full"
        flex="1"
        minHeight="0"
        paddingBlock="1"
        paddingInline="3"
        paddingLeft="6px"
        sx={{
          '.recharts-text': { fill: COLORS.gray500 },
          '.recharts-cartesian-axis-line': { stroke: 'none' },
          '.recharts-cartesian-axis-tick-line, line': { stroke: COLORS.gray100 },
        }}
        overflow="visible"
      >
        <ResponsiveContainer>
          <BarChart
            data={series}
            margin={{ top: 35, right: 24, bottom: 0, left: 0 }}
            syncId={SYNC_ID}
            barCategoryGap="4.5%"
            barGap={0}
            onMouseMove={(state) => onHoverCompetence(state?.activeLabel)}
            onMouseLeave={() => onHoverCompetence(undefined)}
          >
            <CartesianGrid vertical={false} strokeWidth={1} />
            <XAxis
              dataKey="competence"
              tickFormatter={(value) => DateTime.fromISO(value).toFormat('MM/yy')}
              tickLine={false}
              axisLine={false}
              interval={0}
              angle={-55}
              textAnchor="end"
              height={68}
              tick={{ fontSize: 12 }}
            />
            <YAxis
              tickFormatter={formatMwmTick}
              width={Y_AXIS_WIDTH}
              tickMargin={18}
              tickLine={false}
              axisLine={false}
              padding={{ top: 35 }}
              label={<CounterpartyChartYAxisUnitLabel value="MWm" fill={COLORS.gray500} />}
            />
            {yearReferenceLines.map(({ year, competence }) => (
              <ReferenceLine
                key={`${year}-${competence}`}
                x={competence}
                stroke={`${gray900}29`}
                strokeWidth={2}
                ifOverflow="discard"
                isFront={false}
                strokeDasharray="6 6"
                label={{ value: year, position: 'insideTopLeft', fill: COLORS.gray500 }}
              />
            ))}
            <Tooltip content={<ChartTooltip />} cursor={false} />
            {activeCompetence && (
              <ReferenceArea x1={activeCompetence} x2={activeCompetence} fill={`${gray900}14`} stroke="none" />
            )}
            <Bar
              dataKey="value"
              name="MWm"
              radius={[6, 6, 0, 0]}
              barSize={25}
              hide={!hasNonZeroValues}
              isAnimationActive={shouldAnimate}
              animationDuration={500}
            >
              <LabelList
                dataKey="value"
                position="top"
                offset={10}
                fontSize=".625rem"
                formatter={(value) => (shouldRenderValueLabel(Number(value)) ? formatMwmLabel(Number(value)) : '')}
                style={{ fill: COLORS.gray500 }}
              />
              {series.map((point, index) => (
                <Cell key={`bar-${index}`} fill={point.value < 0 ? COLORS.orange500 : COLORS.blue500} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Flex>
    </Flex>
  );
}

export default CounterpartyMwmChart;
